import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';

import { transactionsTypes, transactionsUtils } from 'app-components/flux/transactions';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import TextField from '@mui/material/TextField';
import { makeStyles } from 'tss-react/mui';

import HighlightText from 'components/HighlightText';

const groups = {
  basicOperations: '',
  reinvest: 'Reinvest',
  income: 'Income (Div Int, Dist)',
  shareOperations: '',
  others: '',
  shorts: '',
  cashFlow: '',
};

const options = [
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.BUY],
    group: 'basicOperations',
    id: transactionsTypes.InvestmentTxnType.BUY,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.SELL],
    group: 'basicOperations',
    id: transactionsTypes.InvestmentTxnType.SELL,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.STOCK_DIVIDEND],
    group: 'basicOperations',
    id: transactionsTypes.InvestmentTxnType.STOCK_DIVIDEND,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.REINVEST_DIVIDEND],
    group: 'reinvest',
    id: transactionsTypes.InvestmentTxnType.REINVEST_DIVIDEND,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.REINVEST_INTEREST],
    group: 'reinvest',
    id: transactionsTypes.InvestmentTxnType.REINVEST_INTEREST,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.REINVEST_CAPITAL_GAIN_LONG],
    group: 'reinvest',
    id: transactionsTypes.InvestmentTxnType.REINVEST_CAPITAL_GAIN_LONG,
  },
  {
    label:
      transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.REINVEST_CAPITAL_GAIN_SHORT],
    group: 'reinvest',
    id: transactionsTypes.InvestmentTxnType.REINVEST_CAPITAL_GAIN_SHORT,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.INCOME_DIVIDEND],
    group: 'income',
    id: transactionsTypes.InvestmentTxnType.INCOME_DIVIDEND,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.INCOME_INTEREST],
    group: 'income',
    id: transactionsTypes.InvestmentTxnType.INCOME_INTEREST,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.CAPITAL_GAIN_LONG],
    group: 'income',
    id: transactionsTypes.InvestmentTxnType.CAPITAL_GAIN_LONG,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.CAPITAL_GAIN_SHORT],
    group: 'income',
    id: transactionsTypes.InvestmentTxnType.CAPITAL_GAIN_SHORT,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.INCOME_MISCELLANEOUS],
    group: 'income',
    id: transactionsTypes.InvestmentTxnType.INCOME_MISCELLANEOUS,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.ADD_SHARES],
    group: 'shareOperations',
    id: transactionsTypes.InvestmentTxnType.ADD_SHARES,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.REMOVE_SHARES],
    group: 'shareOperations',
    id: transactionsTypes.InvestmentTxnType.REMOVE_SHARES,
  },
  // { // TODO: no corresponding InvestmentTxnType
  //   label: transactionsUtils.InvestmentTxnActionsByType.VIRTUAL_TRANSFER_SHARES,
  //   group: 'shareOperations',
  //   id: 'VIRTUAL_TRANSFER_SHARES',
  // },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.BUY_BONDS],
    group: 'others',
    id: transactionsTypes.InvestmentTxnType.BUY_BONDS,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.SELL_BONDS],
    group: 'others',
    id: transactionsTypes.InvestmentTxnType.SELL_BONDS,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.EXPENSE_MARGIN_INTEREST],
    group: 'others',
    id: transactionsTypes.InvestmentTxnType.EXPENSE_MARGIN_INTEREST,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.EXPENSE_MISCELLANEOUS],
    group: 'others',
    id: transactionsTypes.InvestmentTxnType.EXPENSE_MISCELLANEOUS,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.RETURN_OF_CAPITAL],
    group: 'others',
    id: transactionsTypes.InvestmentTxnType.RETURN_OF_CAPITAL,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.STOCK_SPLIT],
    group: 'others',
    id: transactionsTypes.InvestmentTxnType.STOCK_SPLIT,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.SHORT_SELL],
    group: 'shorts',
    id: transactionsTypes.InvestmentTxnType.SHORT_SELL,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType[transactionsTypes.InvestmentTxnType.COVER_SHORT_SELL],
    group: 'shorts',
    id: transactionsTypes.InvestmentTxnType.COVER_SHORT_SELL,
  },
  {
    label: transactionsUtils.InvestmentTxnActionsByType.VIRTUAL_CASH_FLOW,
    group: 'cashFlow',
    id: 'VIRTUAL_CASH_FLOW',
  },
];

const useStyles = makeStyles()((_theme) => ({
  paper: {
    minWidth: 'max-content',
  },
}));

const InvestmentTransactionTypeField = (props) => {
  const { value: valueProp, textFieldProps, ...otherProps } = props;

  const { classes } = useStyles();

  const selectedOption = useMemo(() => options.find((option) => option.id === props.value), [props.value]);

  const [input, setInput] = useState(selectedOption?.label);

  return (
    <Autocomplete
      id='investment-transaction-type-field'
      options={options}
      sx={{ minWidth: 80 }}
      disableClearable
      autoComplete
      autoHighlight
      clearOnBlur
      clearOnEscape
      renderInput={(params) => (
        <TextField
          {...params}
          label='Type'
          {...textFieldProps}
          InputProps={{ ...params.InputProps, ...textFieldProps?.InputProps }}
        />
      )}
      inputValue={input}
      onInputChange={(_event, value, _reason) => setInput(value)}
      renderOption={(optionProps, option, _state) => (
        <ListItem {...optionProps} key={option.id}>
          <HighlightText text={option.label} searchKeys={[input]} />
        </ListItem>
      )}
      groupBy={(option) => option.group}
      renderGroup={(params) =>
        !params.group ? (
          params.children
        ) : (
          <div key={params.group}>
            <Divider />
            {groups[params.group] && <ListItem sx={{ color: 'text.disabled' }}>{groups[params.group]}</ListItem>}
            <Box ml={2}>{params.children}</Box>
          </div>
        )
      }
      {...otherProps}
      value={selectedOption}
      onChange={(_event, value, _reason, _details) => props.onChange?.(value?.id)}
      classes={{
        paper: classes.paper,
        ...otherProps.classes,
      }}
    />
  );
};

InvestmentTransactionTypeField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  textFieldProps: PropTypes.object,
};

export default InvestmentTransactionTypeField;
